/**
 * GroupingService
 *
 * A grouping is a JSON file holding the members of a test suite.
 *
 * **Saved with the same folder/file endpoints the rest of the app uses** —
 * `ProjectService.createFolder`, `FileService.saveFileContent` /
 * `getFileContent` / `getFiles` / `deleteFile`. Exactly how
 * `useAutomationSaver` writes `request_payload.json`.
 *
 * There is a `/v1.0/groupings/...` API in the Batch Execution spec, but its
 * API-Gateway routes are not deployed (PR #307) — every call 403s on the CORS
 * preflight. Saving must keep working in the meantime, so persistence stays on
 * the file endpoints. **Only run and cancel go to the batch API.** Do not
 * "upgrade" this to the grouping API without checking the routes are live.
 *
 * Layout — a plain JSON file beside the script folders:
 *   {projectName}/Automation Scripts/{NAME}.json
 * and the path handed to the runner has the same shape as a script's
 * `runnableFilePath`:
 *   projects/{projectId}/{projectName}/Automation Scripts/{NAME}.json
 *
 * Because the client writes the file, the client owns that path. (The spec's
 * "server-owned path" rule applies to the API that isn't deployed.)
 *
 * `saveAs` creates a NEW grouping. Changing an existing one — adding scripts
 * (`addMembers`), taking them out (`removeMembers`), reordering
 * (`reorderMembers`) — rewrites that same file, like the enabled flag.
 *
 * @module shared/middleware/services/GroupingService
 */

import { FileService } from './FileService';
import { ProjectService } from './ProjectService';
import { ProjectHeadersService } from './ProjectHeadersService';
import { fromFileScriptTypes, knownScriptTypes, toFileScriptTypes } from '../../../features/executionStudio/utils/scriptTypes';
import ScriptMetadataService from '../../../features/testSuites/services/metadata/ScriptMetadataService';
import logger from '../../services/logging/logger';

export const GROUPING_SCHEMA_VERSION = 1;

/**
 * Groupings sit as plain JSON files directly in the Automation Scripts folder,
 * alongside the script folders they reference.
 *
 * Safe on both sides of the listing: `getFiles` returns only the
 * current level, so a script's nested `request_payload.json` is never mistaken
 * for a grouping; and the scripts grid filters `directory === true`, so a
 * grouping file never shows up as a script.
 */
export const GROUPING_FOLDER = 'Automation Scripts';

/**
 * Mint a grouping id. Nothing server-side creates one any more.
 *
 * It must stay stable for the life of the grouping: a rename keeps it, an edit
 * keeps it, and save-as-new gets a fresh one because that is a different
 * grouping. The Core writes reports to
 * `…/groupings/{groupingId}/runs/run{N}/`, so an id that changed on rename
 * would orphan the report history.
 */
const mintGroupingId = () => {
  const random = (typeof crypto !== 'undefined' && crypto.getRandomValues)
    ? Array.from(crypto.getRandomValues(new Uint8Array(4)), (b) => b.toString(16).padStart(2, '0')).join('')
    : Math.random().toString(16).slice(2, 10).padEnd(8, '0');
  return `grp-${random}`;
};

/** `OTP regression` → `OTP_REGRESSION`, so the file name is path-safe. */
/**
 * Other app files that live in the Automation Scripts folder next to the
 * groupings. Never list, read or delete them as groupings: listing
 * `script_metadata.json` once showed an empty "group", and deleting that group
 * wiped every script's type tags.
 */
const RESERVED_FILES = new Set([
  'script_metadata.json',
  '_tc_import_index.json',
  '_tc_removal_snapshot.json',
  '_qis_summary.json',
]);

/** Reserved by name, or an underscore-prefixed app file. */
export const isReservedFile = (fileName) => {
  const lower = String(fileName || '').toLowerCase();
  return RESERVED_FILES.has(lower) || lower.startsWith('_');
};

/** A grouping document always carries a name and a scripts array. */
const isGroupingDocument = (doc) =>
  !!doc && typeof doc === 'object' && !Array.isArray(doc)
  && typeof doc.name === 'string' && doc.name.trim().length > 0
  && Array.isArray(doc.scripts);

const toFileSlug = (name) =>
  String(name).trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const fileNameFor = (name) => `${toFileSlug(name)}.json`;

/** Path inside the project, as FileService expects it. */
const filePathFor = (projectName, name) => `${projectName}/${GROUPING_FOLDER}/${fileNameFor(name)}`;

/** Absolute path, matching how a script's runnableFilePath is built. */
const runnablePathFor = (projectId, projectName, name) =>
  `projects/${projectId}/${projectName}/${GROUPING_FOLDER}/${fileNameFor(name)}`;

/**
 * Reads bypass the folder-listing cache. A grouping is usually read moments
 * after being written — on the redirect straight after Save — and a cached
 * listing would not contain it yet.
 */
const FRESH = { noCache: true };

/** Shape a studio member for the file. Array order is execution order. */
const toStoredScript = (member) => ({
  name: member.name,
  runnableFilePath: member.runnableFilePath,
  // Lowercase and sorted — the Middleware routes queue tiers off these
  // An empty scriptTypes array is truthy, so fall through to tags explicitly
  scriptTypes: toFileScriptTypes(member.scriptTypes?.length ? member.scriptTypes : member.tags),
  // Exclusion is part of the saved grouping, not a per-run flag
  enabled: member.enabled !== false,
  // Display cache only — never used for a decision
  permutationCount: member.permutationCount ?? null,
});

/**
 * The project's script metadata (`script_metadata.json`), keyed by upper-cased
 * script name. It is the source of truth for what types a script has — the
 * caller's in-memory tags can be empty if they had not loaded yet, which once
 * saved groupings with `scriptTypes: []`. Never throws; `{}` on any failure.
 */
const readLiveScriptTypes = async (projectId, projectName, userData) => {
  const metadata = await ScriptMetadataService.getMetadata(projectId, projectName, userData);
  const byName = {};
  Object.entries(metadata || {}).forEach(([scriptName, types]) => {
    if (Array.isArray(types)) byName[scriptName.toUpperCase()] = types;
  });
  return byName;
};

/** Prefer the metadata's types; keep what the caller passed when it has none. */
const withLiveScriptTypes = (member, liveTypes) => {
  const fromMetadata = knownScriptTypes(liveTypes[String(member.name || '').toUpperCase()] || []);
  return fromMetadata.length > 0 ? { ...member, scriptTypes: fromMetadata, tags: undefined } : member;
};

/**
 * Parse a stored grouping file.
 *
 * `FileService.getFileContent` returns a file *object*, and its `content` is
 * **base64** — only `decodedContent` is text. Reading `content` directly yields
 * base64 that fails to parse, which once made a freshly saved grouping come
 * back as "not found".
 */
const parseGrouping = (raw) => {
  if (!raw) return null;

  const text = typeof raw === 'string'
    ? raw
    : raw.decodedContent
      ?? (typeof raw.content === 'string' ? FileService.decodeBase64Content(raw.content) : null);

  if (typeof text !== 'string' || !text.trim().startsWith('{')) return null;

  let parsed;
  try { parsed = JSON.parse(text); } catch { return null; }
  // Valid JSON that is not a grouping (e.g. script_metadata.json) is not one
  if (!isGroupingDocument(parsed)) return null;

  return {
    ...parsed,
    scripts: (parsed.scripts || []).map((script) => ({
      ...script,
      scriptTypes: fromFileScriptTypes(script.scriptTypes),
    })),
  };
};

export const GroupingService = {
  /** Every grouping in the project. */
  list: async (projectId, projectName, userData) => {
    let files = [];
    try {
      // Same one-level listing the scripts grid uses. NOT listFilesByPrefix:
      // that one is recursive server-side, so a heavily-run project returns
      // every run screenshot, blows Lambda's 6 MB response limit and comes
      // back empty (P803 — ~105k keys).
      files = await FileService.getFiles(projectId, `${projectName}/${GROUPING_FOLDER}`, userData, FRESH);
    } catch (err) {
      // Folder not created yet — an empty library, not an error
      logger.debug('Grouping folder not readable yet', { error: err.message, projectId });
      return [];
    }

    const jsonFiles = (files || []).filter(
      (f) => !f.directory && (f.name || '').toLowerCase().endsWith('.json') && !isReservedFile(f.name)
    );

    const loaded = await Promise.allSettled(
      jsonFiles.map(async (file) => {
        const raw = await FileService.getFileContent(
          projectId, `${projectName}/${GROUPING_FOLDER}/${file.name}`, userData, FRESH
        );
        const grouping = parseGrouping(raw);
        if (!grouping) return null;
        return {
          ...grouping,
          // `fileKey` addresses the file; `groupingId` identifies the grouping
          // across renames. They are deliberately different things.
          fileKey: file.name.replace(/\.json$/i, ''),
          groupingId: grouping.groupingId || null,
          createdAt: grouping.createdAt || file.lastModified || null,
          updatedAt: grouping.updatedAt || file.lastModified || null,
        };
      })
    );

    return loaded.filter((r) => r.status === 'fulfilled' && r.value).map((r) => r.value);
  },

  /**
   * One grouping, addressed by its **file key** (the slug of its name), which is
   * what the route carries. The grouping's own `groupingId` lives in the file
   * and survives renames.
   */
  get: async (fileKey, projectId, projectName, userData) => {
    if (isReservedFile(`${fileKey}.json`)) throw new Error(`"${fileKey}" is not a grouping.`);
    const raw = await FileService.getFileContent(
      projectId, `${projectName}/${GROUPING_FOLDER}/${fileKey}.json`, userData, FRESH
    );
    const grouping = parseGrouping(raw);
    if (!grouping) throw new Error(`Could not read grouping "${fileKey}".`);
    // Routed by file key; the grouping's own id comes from the file
    return { ...grouping, fileKey };
  },

  /**
   * Past batches for a grouping. No history endpoint is reachable yet, so this
   * returns nothing rather than pretending — the HISTORY tab shows its empty
   * state. Wire up `GET /groupings/{id}/batches` once PR #307 is deployed.
   */
  listBatches: async () => ({ items: [], nextCursor: null }),

  /** Save the working list as a NEW grouping file (BR-24). */
  saveAs: async (projectId, projectName, { name, members }, userData) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('A grouping needs a name.');
    if (isReservedFile(fileNameFor(trimmed))) throw new Error(`"${trimmed}" is a reserved name — choose another.`);
    if (!projectName) throw new Error('A grouping needs its project name to build the file path.');

    // A member without a path is a grouping the runner cannot execute — fail
    // here rather than write a file that only breaks at Run.
    const pathless = (members || []).filter((m) => !m.runnableFilePath).map((m) => m.name);
    if (pathless.length > 0) {
      throw new Error(`These scripts have no runnableFilePath and cannot be saved: ${pathless.join(', ')}`);
    }

    const now = new Date().toISOString();
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    // The folder is one of the project defaults, but create it if this project
    // predates that — a failure here just means it already exists.
    try {
      await ProjectService.createFolder(projectId, projectName, GROUPING_FOLDER, userData);
    } catch (err) {
      logger.debug('Grouping folder already present or not creatable', { error: err.message });
    }

    const document = {
      schemaVersion: GROUPING_SCHEMA_VERSION,
      // A fresh id: save-as-new is a different grouping (BR-24)
      groupingId: mintGroupingId(),
      name: trimmed,
      tenantId: ProjectHeadersService.getTenantId(userData),
      divisionId: userData?.divisionId ?? null,
      projectId,
      projectName,
      // We write the file, so we own the path the runner is given
      runnableGroupPath: runnablePathFor(projectId, projectName, trimmed),
      createdBy: userData?.userId ?? null,
      createdAt: now,
      updatedBy: userData?.userId ?? null,
      updatedAt: now,
      scripts: (members || []).map((m) => toStoredScript(withLiveScriptTypes(m, liveTypes))),
    };

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, trimmed), JSON.stringify(document, null, 2), userData
    );

    logger.info('Grouping saved', { projectId, name: trimmed, groupingId: document.groupingId, scripts: document.scripts.length });
    return { ...document, fileKey: toFileSlug(trimmed) };
  },

  /**
   * Rename — name only, never members (BR-25).
   *
   * Writes the new file before removing the old one: a failed delete leaves a
   * recoverable duplicate, where a failed write after a delete loses the lot.
   */
  rename: async (groupingId, newName, projectId, projectName, userData) => {
    const trimmed = String(newName || '').trim();
    if (!trimmed) throw new Error('A grouping needs a name.');
    if (isReservedFile(fileNameFor(trimmed))) throw new Error(`"${trimmed}" is a reserved name — choose another.`);

    const existing = await GroupingService.get(groupingId, projectId, projectName, userData);
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    const document = {
      ...existing,
      // groupingId is deliberately carried over — a rename is the same grouping,
      // and a changed id would orphan its report history in S3.
      name: trimmed,
      runnableGroupPath: runnablePathFor(projectId, projectName, trimmed),
      updatedBy: userData?.userId ?? null,
      updatedAt: new Date().toISOString(),
      // Re-serialise: the loaded copy carries app-token scriptTypes, and writing
      // those straight back would corrupt the lowercase form the Middleware
      // routes queue tiers on. Also backfills types an older save left empty.
      scripts: (existing.scripts || []).map((script) => toStoredScript(withLiveScriptTypes(script, liveTypes))),
    };

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, trimmed), JSON.stringify(document, null, 2), userData
    );

    if (toFileSlug(trimmed) !== toFileSlug(existing.name)) {
      try {
        await FileService.deleteFile(projectId, filePathFor(projectName, existing.name), userData);
      } catch (err) {
        logger.warn('Renamed grouping saved, but the old file could not be removed', {
          error: err.message, groupingId,
        });
      }
    }

    logger.info('Grouping renamed', { groupingId: document.groupingId, name: trimmed });
    return { ...document, fileKey: toFileSlug(trimmed) };
  },

  /** Toggle one member's enabled flag — a flag, not membership, so in place. */
  setMemberEnabled: async (groupingId, scriptName, enabled, projectId, projectName, userData) => {
    const existing = await GroupingService.get(groupingId, projectId, projectName, userData);
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    const document = {
      ...existing,
      // Re-serialised for the same reason as rename — see above
      scripts: (existing.scripts || []).map((script) =>
        toStoredScript(withLiveScriptTypes(script.name === scriptName ? { ...script, enabled } : script, liveTypes))
      ),
      updatedBy: userData?.userId ?? null,
      updatedAt: new Date().toISOString(),
    };

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, existing.name), JSON.stringify(document, null, 2), userData
    );
    return document;
  },

  /**
   * Add scripts to a grouping, in place — appended after the existing ones, in
   * the order given. A script already in the grouping is not added twice.
   */
  addMembers: async (groupingId, newMembers, projectId, projectName, userData) => {
    const existing = await GroupingService.get(groupingId, projectId, projectName, userData);
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    const have = new Set((existing.scripts || []).map((s) => String(s.name).toUpperCase()));
    const additions = (newMembers || []).filter((m) => {
      const key = String(m.name).toUpperCase();
      if (have.has(key)) return false;
      have.add(key);
      return true;
    });

    const pathless = additions.filter((m) => !m.runnableFilePath).map((m) => m.name);
    if (pathless.length > 0) {
      throw new Error(`These scripts have no runnableFilePath and cannot be added: ${pathless.join(', ')}`);
    }

    const document = {
      ...existing,
      scripts: [...(existing.scripts || []), ...additions]
        .map((script) => toStoredScript(withLiveScriptTypes(script, liveTypes))),
      updatedBy: userData?.userId ?? null,
      updatedAt: new Date().toISOString(),
    };
    delete document.fileKey;

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, existing.name), JSON.stringify(document, null, 2), userData
    );
    logger.info('Scripts added to grouping', { groupingId, added: additions.length });
    return document;
  },

  /**
   * Take scripts out of a grouping, in place — the file simply stops listing
   * them. The scripts themselves are untouched in Automation Scripts. Passing
   * every name clears the grouping; an empty grouping is allowed.
   */
  removeMembers: async (groupingId, names, projectId, projectName, userData) => {
    const existing = await GroupingService.get(groupingId, projectId, projectName, userData);
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    const drop = new Set((names || []).map((n) => String(n).toUpperCase()));
    const kept = (existing.scripts || []).filter((s) => !drop.has(String(s.name).toUpperCase()));

    const document = {
      ...existing,
      scripts: kept.map((script) => toStoredScript(withLiveScriptTypes(script, liveTypes))),
      updatedBy: userData?.userId ?? null,
      updatedAt: new Date().toISOString(),
    };
    delete document.fileKey;

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, existing.name), JSON.stringify(document, null, 2), userData
    );
    logger.info('Scripts removed from grouping', { groupingId, removed: drop.size, remaining: kept.length });
    return document;
  },

  /**
   * Change the run order of a grouping's scripts, in place. Array order is
   * execution order. Membership is untouched: names not in `orderedNames` keep
   * their relative order at the end rather than being dropped.
   */
  reorderMembers: async (groupingId, orderedNames, projectId, projectName, userData) => {
    const existing = await GroupingService.get(groupingId, projectId, projectName, userData);
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    const scripts = existing.scripts || [];
    const rank = new Map((orderedNames || []).map((name, i) => [String(name).toUpperCase(), i]));
    const reordered = scripts
      .map((script, i) => ({ script, i, r: rank.get(String(script.name).toUpperCase()) }))
      .sort((a, b) => (a.r ?? Infinity) - (b.r ?? Infinity) || a.i - b.i)
      .map(({ script }) => script);

    const document = {
      ...existing,
      scripts: reordered.map((script) => toStoredScript(withLiveScriptTypes(script, liveTypes))),
      updatedBy: userData?.userId ?? null,
      updatedAt: new Date().toISOString(),
    };
    delete document.fileKey;

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, existing.name), JSON.stringify(document, null, 2), userData
    );
    return document;
  },

  /**
   * Write the members' run order and enabled flags in one go — what the
   * grouping page's autosave sends. `members` is the page's list, in order, as
   * `{ name, enabled }`.
   *
   * One write carrying the whole list, rather than one read-modify-write per
   * toggle: two quick toggles each read the same file, and the second write
   * silently undid the first. Scripts in the file but not in the list keep
   * their place after the listed ones and their own enabled flag.
   */
  saveMembers: async (groupingId, members, projectId, projectName, userData) => {
    const existing = await GroupingService.get(groupingId, projectId, projectName, userData);
    const liveTypes = await readLiveScriptTypes(projectId, projectName, userData);

    const wanted = new Map((members || []).map((m, i) => [String(m.name).toUpperCase(), { i, enabled: m.enabled }]));
    const scripts = (existing.scripts || [])
      .map((script, i) => ({ script, i, w: wanted.get(String(script.name).toUpperCase()) }))
      .sort((a, b) => (a.w?.i ?? Infinity) - (b.w?.i ?? Infinity) || a.i - b.i)
      .map(({ script, w }) => (w && typeof w.enabled === 'boolean' ? { ...script, enabled: w.enabled } : script));

    const document = {
      ...existing,
      scripts: scripts.map((script) => toStoredScript(withLiveScriptTypes(script, liveTypes))),
      updatedBy: userData?.userId ?? null,
      updatedAt: new Date().toISOString(),
    };
    delete document.fileKey;

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, existing.name), JSON.stringify(document, null, 2), userData
    );
    return document;
  },

  /**
   * Stamp run metadata onto the grouping file (BRS §6.2.1).
   *
   * We own these four fields — `status`, `lastRunAt`, `lastBatchId`,
   * `lastPassRate` — and they live alongside the existing ones. The Middleware
   * ignores fields it does not know, so nothing breaks on its side.
   *
   * **A run is not an edit.** `updatedAt` / `updatedBy` mean "someone changed
   * the membership"; stamping a run deliberately leaves them alone, or every
   * grouping would look edited when it was only run.
   *
   * Read-modify-write of the whole file. Pass `loaded` when the caller already
   * has the grouping in hand — at submit it does, so the read is free.
   *
   * @param {Object} run  { status, lastRunAt, lastBatchId, lastPassRate }
   */
  stampRun: async (fileKey, projectId, projectName, run, userData, loaded = null) => {
    const existing = loaded || await GroupingService.get(fileKey, projectId, projectName, userData);

    const document = {
      ...existing,
      scripts: (existing.scripts || []).map(toStoredScript),
      // Only the four run fields move. updatedAt/updatedBy are untouched.
      status: run.status ?? existing.status ?? null,
      lastRunAt: run.lastRunAt ?? existing.lastRunAt ?? null,
      lastBatchId: run.lastBatchId ?? existing.lastBatchId ?? null,
      lastPassRate: run.lastPassRate ?? existing.lastPassRate ?? null,
    };
    delete document.fileKey;

    await FileService.saveFileContent(
      projectId, filePathFor(projectName, existing.name), JSON.stringify(document, null, 2), userData
    );
    logger.info('Grouping run stamped', { fileKey, status: document.status, batchId: document.lastBatchId });
    return { ...document, fileKey };
  },

  /** Delete the grouping file. Never touches the scripts it referenced (BR-26). */
  remove: async (groupingId, projectId, projectName, userData) => {
    // Confirm the file really is a grouping before deleting anything — a
    // mis-listed app file must never be deletable from the groups screens.
    // get() throws for reserved names and for files that are not groupings.
    await GroupingService.get(groupingId, projectId, projectName, userData);

    await FileService.deleteFile(
      projectId, `${projectName}/${GROUPING_FOLDER}/${groupingId}.json`, userData
    );
    logger.info('Grouping deleted', { projectId, groupingId });
    return true;
  },

  /**
   * Which groupings each of `scriptNames` is a member of — enabled or not — so a
   * script still used by a grouping cannot be deleted from under it.
   *
   * Strict where `list` is forgiving: a grouping file that cannot be read throws
   * rather than being skipped, because skipping it could let a script in that
   * grouping be deleted. A project with no groupings folder has no groupings.
   *
   * @returns {Promise<Object<string, string[]>>} script name → grouping names, for
   *   the scripts that are in at least one grouping only
   */
  groupingsContaining: async (projectId, projectName, scriptNames, userData) => {
    let files = [];
    try {
      files = await FileService.getFiles(projectId, `${projectName}/${GROUPING_FOLDER}`, userData, FRESH);
    } catch (err) {
      logger.debug('Grouping folder not readable yet — no groupings to check', { error: err.message, projectId });
      return {};
    }
    const jsonFiles = (files || []).filter(
      (f) => !f.directory && (f.name || '').toLowerCase().endsWith('.json') && !isReservedFile(f.name)
    );
    const groupings = await Promise.all(jsonFiles.map(async (file) => {
      const raw = await FileService.getFileContent(
        projectId, `${projectName}/${GROUPING_FOLDER}/${file.name}`, userData, FRESH
      );
      return parseGrouping(raw);
    }));

    const wanted = new Map((scriptNames || []).map((n) => [String(n).toUpperCase(), n]));
    const found = {};
    groupings.forEach((grouping) => {
      if (!grouping) return; // a non-grouping JSON file
      (grouping.scripts || []).forEach((script) => {
        const name = wanted.get(String(script.name || '').toUpperCase());
        if (!name) return;
        const list = found[name] || (found[name] = []);
        if (!list.includes(grouping.name)) list.push(grouping.name);
      });
    });
    return found;
  },
};

/**
 * Why a delete is refused, naming every grouping to remove the script(s) from.
 * @param {Object<string, string[]>} conflicts script name → grouping names
 */
export const describeDeleteBlockedByGroupings = (conflicts) => {
  const entries = Object.entries(conflicts || {});
  const quoted = (names) => names.map((n) => `"${n}"`).join(', ');
  if (entries.length === 1) {
    const [script, groups] = entries[0];
    return groups.length === 1
      ? `"${script}" is in the grouping ${quoted(groups)}. Remove it from that grouping before deleting it.`
      : `"${script}" is in ${groups.length} groupings: ${quoted(groups)}. Remove it from those groupings before deleting it.`;
  }
  const detail = entries.map(([script, groups]) => `"${script}" (${groups.join(', ')})`).join('; ');
  return `${entries.length} of these scripts are in groupings: ${detail}. Remove them from those groupings before deleting.`;
};

export default GroupingService;
